import {
  IQueuedOperation,
  IOperationPayload,
  getOperationTypeKey,
  getOperationState,
  editQueue,
  OperationTypeKey
} from '../store/actions/implementOperation';
import { getItemsOperation } from './getItemsOperation';
import { Signal } from '../utilities/async/Signal';
import { subtract, isSubset, DefaultPatch, Patch } from '../utilities/Graft';
import { IFaceted, pack, IFacet } from '../models/Facet';
import { pickObject, makeObject, mapObject } from '../utilities/objects';
import { IItemCacheThunkAction, IItemCacheDispatch } from '../store/actions/ItemCacheActions';
import { resolvedFacet, DemandResolution } from '../facets/DemandFacets';
import {
  supportedFacetsItemKeyFacet,
  supportedFacetsFacet,
} from '../facets/OperationFacets';
import { KeyFacet, Key } from '../models/KeyFacet';
import { registerPermanentItemDemands } from './pruneItems';
import { updateItems } from '../store/reducers/itemMapReducer';
import { IAsyncActionCreator, createAsyncActionCreator } from '../store/actions/createActionCreator';
import { IActionWithEffect, processBatchWithEffects } from './processBatchWithEffects';
import { processBatch } from '../store/reducers/itemCacheReducer';

/**
 * Given a queue of pending operations as well as a completed batch operation,
 * produces a revised queue in which the remaining pending operations
 * have no intersection with the completed operation.
 */
function cleanupResolvedGetItemsOperations<TKey extends object>(
  currentOperations: IQueuedOperation<TKey, typeof getItemsOperation.type>[],
  completedOperations: IOperationPayload<typeof getItemsOperation.type>
): {
    newQueue: IQueuedOperation<TKey, typeof getItemsOperation.type>[];
    workToComplete: Signal<void>[];
  } {
  let newCurrentOperations: (typeof currentOperations) | undefined = undefined;

  const workToComplete: Signal<void>[] = [];

  for (let i = 0, length = currentOperations.length; i < length; i++) {
    const currentOperation = currentOperations[i];

    const {
      itemKey,
      completeSignal: currentCompleteSignal,
      options: currentOptions
    } = currentOperation;

    const completedOptions = completedOperations.items[itemKey];

    let remainingOptions: typeof currentOptions;

    if (completedOptions &&
      (remainingOptions = subtract(currentOptions, completedOptions)) &&
      !isSubset(remainingOptions, currentOptions)) {
      // There is a matching completed operation, and subtracing the newly-resolved facets
      // from the original operation has an effect.

      if (!newCurrentOperations) {
        newCurrentOperations = currentOperations.slice(0, i);
      }

      if (Object.keys(remainingOptions).length > 0 && Object.keys(remainingOptions.demands).length > 0) {
        // Add back the part which is still unresolved.
        newCurrentOperations.push({
          ...currentOperation,
          options: remainingOptions
        });
      } else {
        // The current operation is obsolete.
        workToComplete.push(currentCompleteSignal);
      }
    } else {
      // The current operation is untouched.

      if (newCurrentOperations) {
        // Since the list has been modified, ensure the original operation
        // is added back.
        newCurrentOperations.push(currentOperation);
      }
    }
  }

  return {
    newQueue: newCurrentOperations || currentOperations,
    workToComplete: workToComplete
  };
}

/**
 * Processes a queue of operations and prunes any demands which are out of the
 * range of 'supported' demands.
 * Any operation which winds up with no valid demands is removed from the resulting queue,
 * and a signal is emitted for each to be marked as complete.
 */
function removeUnsupportedFacets<TKey extends object>(
  currentOperations: IQueuedOperation<TKey, typeof getItemsOperation.type>[],
  demands: IFaceted<boolean>
): {
    newQueue: IQueuedOperation<TKey, typeof getItemsOperation.type>[];
    workToComplete: Signal<void>[];
  } {
  let newCurrentOperations: (typeof currentOperations) | undefined = undefined;

  const workToComplete: Signal<void>[] = [];

  for (let i = 0, length = currentOperations.length; i < length; i++) {
    const currentOperation = currentOperations[0];

    const {
      completeSignal,
      options: {
        demands: currentDemands
      }
    } = currentOperation;

    let newOperation: (typeof currentOperation | undefined) = undefined;

    const filteredDemands = pickObject(currentDemands, demands) as IFaceted<boolean>;

    if (filteredDemands !== currentDemands) {
      newOperation = {
        ...currentOperation,
        options: {
          ...currentOperation.options,
          demands: filteredDemands
        }
      };
    }

    if (newOperation) {
      if (!newCurrentOperations) {
        newCurrentOperations = currentOperations.slice(0, i);
      }

      if (Object.keys(newOperation.options.demands).length > 0) {
        newCurrentOperations.push(newOperation);
      } else {
        // Exclude the operation.
        workToComplete.push(completeSignal);
      }
    } else if (newCurrentOperations) {
      newCurrentOperations.push(currentOperation);
    }
  }

  return {
    newQueue: newCurrentOperations || currentOperations,
    workToComplete: workToComplete
  };
}

/**
 * Payload for the `supportFacets` action creator.
 * @public
 */
export interface ISupportFacetsPayload<TKey extends object> {
  /**
   * A key facet for which to filter the list of allowed facet demands.
   */
  keyFacet: KeyFacet<TKey>;
  /**
   * A set of facets for which to permit requests for items.
   * These will be appended to the existing allowed facets for the key facet.
   */
  facets: IFacet<{}>[];
}

/**
 * Creates an action which, when dispatched, specifies that items with the given KeyFacet
 * support only requests which demand at least one of the specified facets.
 *
 * An operation handler backing the specified KeyFacet may resolve more facets on any given item
 * than requested. However, the UX layer will only issue new requests if at least one of the supported
 * facets is in an unresolved state.
 *
 * Use this operation to ensure that operation handlers are not issued spurious requests to resolve
 * facets they can never support.
 * @public
 */
export function supportFacets<TKey extends object>(payload: ISupportFacetsPayload<TKey>): IItemCacheThunkAction<void> {
  const {
    keyFacet,
    facets
  } = payload;

  const itemKey = supportedFacetsItemKeyFacet.serialize({
    keyFacetId: keyFacet.id
  });

  const operationTypeKey = getOperationTypeKey({
    operationTypeFacet: getItemsOperation,
    keyFacet: keyFacet
  });

  const supportedFacets: IFaceted<boolean> = {
    ...pack({}, {}),
    ...makeObject(facets.map((facet: IFacet<{}>) => facet.id), () => true)
  };

  return (dispatch: IItemCacheDispatch) => {
    dispatch(registerPermanentItemDemands({
      items: {
        [itemKey]: {}
      }
    }));

    dispatch(updateItems({
      items: {
        [itemKey]: {
          ...supportedFacetsFacet.pack({
            demands: supportedFacets
          })
        }
      }
    }));

    const operationState = dispatch(getOperationState({
      key: operationTypeKey
    }));

    const queuedOperations = operationState && operationState.queue;

    if (queuedOperations && queuedOperations.length > 0) {
      const {
        newQueue: queuePatchOperation,
        workToComplete
      } = removeUnsupportedFacets(queuedOperations, supportedFacets);

      dispatch(editQueue({
        key: operationTypeKey,
        queue: queuePatchOperation
      }));

      for (const signal of workToComplete) {
        // Mark the work as completed, since future requests would just be a no-op.
        signal.resolve();
      }
    }
  };
}

/**
 * Patch for an array of queued operations which, when applied,
 * produces a series of signals exracted from the operations
 * which are now considered `complete` because there was a matching input operation.
 */
class CleanupQueuePatch extends Patch<IQueuedOperation<object, object>[]> {
  private _completedOperation: IOperationPayload<typeof getItemsOperation.type>;
  private _updateCompleteSignals: (completeSignals: Signal<void>[]) => void;

  constructor(
    completedOperation: IOperationPayload<typeof getItemsOperation.type>,
    updateCompleteSignals: (completeSignals: Signal<void>[]) => void
  ) {
    super();

    this._completedOperation = completedOperation;
    this._updateCompleteSignals = updateCompleteSignals;
  }

  public apply(
    value: IQueuedOperation<object, object>[] | undefined
  ): IQueuedOperation<object, object>[] | undefined {
    if (value) {
      const {
        newQueue: queuePatchOperation,
        workToComplete
      } = cleanupResolvedGetItemsOperations(
        value as IQueuedOperation<object, typeof getItemsOperation.type>[],
        this._completedOperation
      );

      this._updateCompleteSignals(workToComplete);

      return queuePatchOperation;
    }
  }
}

/**
 * Creates an action which, when dispatched, produces the necessary steps to resolve items
 * as part of a batch.
 * See `resolveItems` for a description of the behavior.
 *
 * This 'batched' version is intended to have its output passed to `processBatchWithEffects`, which merges
 * multiple actions into a single store update.
 * @internal
 */
export const resolveItemsForBatch: IAsyncActionCreator<typeof updateItems.payloadType, IItemCacheThunkAction<IActionWithEffect>> =
  createAsyncActionCreator((payload: typeof updateItems.payloadType) => {
    const {
      items
    } = payload;

    const completedOperationsByKey: {
      [operationTypeKey: string]: IOperationPayload<typeof getItemsOperation.type>;
    } = {};

    const resolvedItems: {
      [itemKey: string]: IFaceted;
    } = {};

    for (const itemKey in items) {
      if (Object.prototype.hasOwnProperty.call(items, itemKey)) {
        const keyFacet = KeyFacet.get(itemKey);

        const operationTypeKey = getOperationTypeKey({
          operationTypeFacet: getItemsOperation,
          keyFacet: keyFacet
        });

        const patch = items[itemKey];

        const options: typeof getItemsOperation.type = {
          // Note: the value provided for any facet could be a patch operation,
          // including one which deletes the facet. However, for the moment,
          // consider any operation for a specific facet to be a resolution of that facet.
          demands: patch && typeof patch === 'object' ?
            mapObject(<IFaceted>patch, () => true) :
            pack({}, {})
        };

        resolvedItems[itemKey] = {
          [keyFacet.id]: new DefaultPatch(keyFacet.deserialize(itemKey as Key<{}>)),
          ...resolvedFacet.pack({
            demands: {
              [keyFacet.id]: DemandResolution.resolved,
              ...mapObject(options.demands, () => DemandResolution.resolved)
            }
          })
        };

        let completedOperation = completedOperationsByKey[operationTypeKey];

        if (!completedOperation) {
          completedOperationsByKey[operationTypeKey] = completedOperation = {
            items: {}
          };
        }

        completedOperation.items[itemKey] = options;
      }
    }

    const completeSignals: Signal<void>[] = [];

    return () => {
      return {
        action: processBatch({
          actions: [
            // Resolution of facets needs to be committed before
            // the new values for the facets. This prevents aggressive store
            // subscriptions from re-queuing actions in response to the updates.
            updateItems({
              items: resolvedItems
            }),
            updateItems(payload),
            ...(<OperationTypeKey<object, typeof getItemsOperation.type>[]>Object.keys(completedOperationsByKey)).map((
              operationTypeKey: OperationTypeKey<object, typeof getItemsOperation.type>
            ) => {
              const completedOperation = completedOperationsByKey[operationTypeKey];

              return editQueue({
                key: operationTypeKey,
                queue: new CleanupQueuePatch(completedOperation, (workToComplete: Signal<void>[]) => {
                  completeSignals.push(...workToComplete);
                })
              });
            })
          ]
        }),
        effect: () => {
          for (const completeSignal of completeSignals) {
            // Mark all removed work as completed.
            completeSignal.resolve();
          }
        }
      };
    };
  });

/**
 * Applies the given item updates to the cache and marks every mentioned
 * facet as resolved.
 *
 * When dispatched, `resolveItems` will immediately modify the store to reflect the
 * specified resolutions. All specified items will have the new facets merged with existing facet values.
 * For each item, each provided facet will also be marked as 'resolved', indicating that it will not need
 * to be fetched by its backing operation handler.
 *
 * Any existing operations queued for a given item will be pruned of 'resolved' demands, ensuring that
 * redundant requests are not subsequently issued.
 * @public
 */
export const resolveItems: IAsyncActionCreator<typeof updateItems.payloadType, IItemCacheThunkAction<void>> =
  createAsyncActionCreator((payload: typeof updateItems.payloadType): IItemCacheThunkAction<void> => {
    return processBatchWithEffects({
      actionsWithEffects: [resolveItemsForBatch(payload)]
    });
  });
